/**
 * One commit as a history row (RFC 0313 3n): its subject, sha and size, and,
 * opened, the files it changed, each opening at that commit.
 *
 * No undo. A commit is undone by another commit, which is the agent's to make.
 */

import { ChevronRight, FileCode2, GitCommitHorizontal } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { codeFileId } from './codeFile'
import {
  type CodeCommit,
  type CodeFileChange,
  commitFiles,
} from './codeHistory'

/** `+3 −1`, green and red, as the rest of the window draws a diff's size. */
export function LineCounts({
  insertions,
  deletions,
}: {
  insertions: number
  deletions: number
}) {
  return (
    <>
      <span className="text-emerald-600">+{insertions}</span>{' '}
      <span className="text-red-600">−{deletions}</span>
    </>
  )
}

export function FileRows({
  files,
  at,
  onOpen,
  sourceId,
}: {
  files: CodeFileChange[]
  /** The ref to open them at; null is the working tree. */
  at: string | null
  onOpen?: (codeId: string) => void
  sourceId?: string
}) {
  return (
    <div className="flex flex-col py-0.5">
      {files.map((f) => (
        <div
          key={f.path}
          title={f.path}
          className="flex items-center gap-2 py-0.5 text-xs"
        >
          <FileCode2 className="size-3 shrink-0 text-muted-foreground/70" />
          {/* A deleted file has nothing to open at this ref. */}
          {onOpen && f.status !== 'deleted' ? (
            <button
              type="button"
              onClick={() => onOpen(codeFileId(f.path, at, sourceId))}
              className="min-w-0 truncate text-left font-mono text-[11px] text-foreground/80 hover:underline"
            >
              {f.path}
            </button>
          ) : (
            <span className="min-w-0 truncate font-mono text-[11px] text-foreground/80">
              {f.path}
            </span>
          )}
          <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
            {f.status === 'modified' ? '' : `${f.status} `}
            <LineCounts insertions={f.insertions} deletions={f.deletions} />
          </span>
        </div>
      ))}
    </div>
  )
}

export function CommitRow({
  projectId,
  commit,
  byline,
  onOpen,
  sourceId,
}: {
  projectId: string
  commit: CodeCommit
  /** What goes after the sha: a branch, a session, a time. */
  byline?: ReactNode
  onOpen?: (codeId: string) => void
  sourceId?: string
}) {
  const [open, setOpen] = useState(false)
  const [files, setFiles] = useState<CodeFileChange[] | null>(null)
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o)
          if (!files)
            void commitFiles(projectId, commit.sha, sourceId).then(
              setFiles,
              () => setFiles([]),
            )
        }}
        aria-expanded={open}
        className="flex w-full items-start gap-2 text-left"
      >
        <GitCommitHorizontal className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <span className="block break-words text-[13px] text-foreground">
            {commit.subject}
          </span>
          <span className="block truncate text-muted-foreground text-xs">
            <span className="font-mono">{commit.shortSha}</span>
            {byline ? <> · {byline}</> : null} · {commit.files} file
            {commit.files === 1 ? '' : 's'}{' '}
            <span className="text-emerald-600">+{commit.insertions}</span>{' '}
            <span className="text-red-600">−{commit.deletions}</span>
          </span>
        </span>
        <ChevronRight
          className={`mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>
      {open ? (
        <div className="pl-5">
          {files === null ? (
            <span className="text-[11px] text-muted-foreground">Reading…</span>
          ) : (
            <FileRows
              files={files}
              at={commit.sha}
              sourceId={sourceId}
              {...(onOpen ? { onOpen } : {})}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}
